/**
 * The words a user reads when a request fails.
 *
 * The server declares 277 error codes. `lib/api.js` acted on six and every
 * other one reached the screen as whatever the calling page did with
 * `data.message` — usually the server's own wording, which
 * `.claude/rules/frontend-ux.md` asks callers not to forward: it leaks
 * internals, and the same failure reads differently on every screen
 * (I1768 F10).
 *
 * Writing 277 handlers would be the wrong answer. One default is the right
 * one: a code this file does not name gets a sentence chosen here, so no
 * server wording reaches a user by accident, and a code worth its own words
 * gets them by being added to `CLIENT_COPY`.
 *
 * `error_code` is the contract, never the message —
 * `.claude/rules/testing.md` says as much, because a product owner rewording
 * a server string must not change what a screen says.
 */

import { FEEDBACK_LIMITS } from "@/config/dropdownData";

/** The fallback. Says what happened and what to do, and names nothing internal. */
export const GENERIC_ERROR = "Something went wrong. Try again, and tell us if it keeps happening.";

/**
 * Codes whose message is written here, for the user.
 *
 * Add a code when a screen needs it to say something specific. Everything
 * absent falls back to {@link GENERIC_ERROR}, which is the point.
 */
export const CLIENT_COPY = {
  INSUFFICIENT_PERMISSIONS: "You don't have permission to do that.",
  COMPANY_SUSPENDED: "This organization's access has been suspended.",
  USER_DEACTIVATED: "This account has been deactivated.",
  DEPLOYMENT_RESTART: "The app was updated. Sign in again to continue.",
  MODULE_NOT_ENABLED: "This feature is not part of your plan.",
  MODULE_TEMPORARILY_UNAVAILABLE: "This feature is briefly unavailable. Try again in a few minutes.",
  RATE_LIMIT_EXCEEDED: "Too many requests. Wait a moment and try again.",
  NOT_FOUND: "That record no longer exists.",
  // Step approval (OUTREACH_OVERHAUL.md §7). The Start button is disabled
  // before the request is ever made, with the specific step numbers on it
  // (see unconfirmedStepsWarning in utils/deleteImpact.js) — this generic
  // sentence is the fallback for the rare case the request still lands.
  EMPTY_APPROVED_BODY: "This step needs a message before it can be confirmed.",
  SEQUENCE_HAS_UNCONFIRMED_STEPS: "Every step needs to be confirmed before the sequence can start.",
  LLM_RESPONSE_ERROR: "The AI couldn't answer just now. Try again in a moment.",
  // A step with a message past `scheduled` is read-only (OUTREACH_OVERHAUL.md §6).
  STEP_LOCKED: "This step has already sent to a candidate, so it can't be changed. Duplicate the sequence to change it.",
  // The Communication Center's Telegram invite refusals.
  TELEGRAM_NOT_CONFIGURED: "Telegram isn't set up for your organisation yet.",
  CANDIDATE_HAS_NO_EMAIL: "This candidate has no email address to send the invite to.",
  TELEGRAM_ALREADY_LINKED: "This candidate is already connected on Telegram.",
  // Product feedback. The form refuses all three first, in the same words;
  // these cover the request that still lands.
  FEEDBACK_TOO_MANY_IMAGES: FEEDBACK_LIMITS.messages.tooManyImages,
  FEEDBACK_IMAGE_INVALID: FEEDBACK_LIMITS.messages.imageInvalid,
  FEEDBACK_IMAGE_TOO_LARGE: FEEDBACK_LIMITS.messages.imageTooLarge,
  FEEDBACK_COMMENT_NEEDS_LETTER: FEEDBACK_LIMITS.messages.commentNeedsLetter,
  FEEDBACK_REASON_NEEDS_LETTER: FEEDBACK_LIMITS.messages.reasonNeedsLetter,
  FEEDBACK_REASON_REQUIRED: "Give a reason. Ending feedback this way needs one.",
  FEEDBACK_TRANSITION_NOT_ALLOWED: "Feedback only moves forward. It can't go back to Open, and an ended feedback can't change.",
  FEEDBACK_STATUS_NOT_ALLOWED: "That status does not apply to this type of feedback.",
  FEEDBACK_TYPE_CHANGE_NOT_ALLOWED: "The type can only be changed while the feedback is Open.",
  FEEDBACK_TENANT_NOT_FOUND: "That tenant no longer exists. Pick another.",
  FEEDBACK_IMPORT_ROWS_INVALID: "Some rows can't be imported. Nothing was saved — fix them and upload again.",
  FEEDBACK_RECIPIENTS_TO_REQUIRED: "Add at least one To recipient.",
  FEEDBACK_RECIPIENT_DUPLICATE: "Each SuperAdmin can appear only once across To, CC and BCC.",
  FEEDBACK_RECIPIENT_NOT_SUPERADMIN: "Only active SuperAdmins can receive feedback emails.",
  FEEDBACK_RECIPIENT_ONLY_TO:
    "They are the only To recipient of feedback emails. Add another SuperAdmin as a To recipient in Feedback → Email settings first, then revoke.",
};

/**
 * Codes whose server message is composed for the person reading it, and is
 * more useful than anything this file could say in advance.
 *
 * Each one carries a value the user needs — the field that failed, the limit
 * they hit, the size they exceeded — so forwarding it is a decision rather
 * than an omission. A code arrives here only when someone has read the
 * server's wording and confirmed it names nothing internal.
 */
export const SERVER_WORDED = new Set([
  "VALIDATION_ERROR",
  "RECORD_EXPORT_TOO_LARGE",
  // Which {{variable}} was unknown is dynamic — the server's composed
  // sentence is what's useful (OUTREACH_OVERHAUL.md §7).
  "UNKNOWN_TEMPLATE_VARIABLE",
  // Says what is wrong with the import file as a whole: its size, its kind,
  // the columns it lacks, or that it has no rows.
  "FEEDBACK_IMPORT_FILE_INVALID",
]);

/**
 * The sentence to show for a failed request.
 *
 * @param {unknown} error  an axios error, or anything
 * @param {string} [fallback]  what to say when nothing else applies
 * @returns {string}
 */
export const messageForError = (error, fallback = GENERIC_ERROR) => {
  const data = error?.response?.data ?? {};
  const code = data.error_code;

  if (code && CLIENT_COPY[code]) return CLIENT_COPY[code];
  if (code && SERVER_WORDED.has(code) && data.message) return data.message;

  return fallback;
};

export default messageForError;
